import type { FC } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button, Paper, StackColumn, useIsMobileVariant, useSnackbar } from '@dhl-ui/components'
import { NativeModal } from '@dhl/native-modal'
import { type Servicepoint } from '@dhl/receiver-servicepoint'

import { useSubmitPreference } from '~/profile/view/preferences/hooks/useSubmitPreference.js'
import { ServicepointCloserByContent } from '~/servicepoints/components/ServicepointCloserByContent.js'

type Props = {
  addressId?: string
  open: boolean
  onClose: () => void
  isLoading: boolean
  servicepoint?: Servicepoint
  typeKey: 'locker' | 'servicepoint'
}

export const CloserByServicePointModal: FC<Props> = ({
  addressId,
  open,
  onClose,
  isLoading,
  servicepoint,
  typeKey,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { t } = useTranslation()
  const { submitPreference } = useSubmitPreference()
  const isMobileVariant = useIsMobileVariant()
  const { enqueueSnackbar } = useSnackbar()

  const handleCtaClick = async () => {
    if (!addressId || !servicepoint) {
      return
    }

    setIsSubmitting(true)

    try {
      await submitPreference(addressId, {
        preferenceType: 'ServicePointDelivery',
        psfKey: servicepoint.servicepointKey,
      })

      enqueueSnackbar(t('portal.profile.preferences.preferenceSavedSuccessfully'), { severity: 'success' })
      onClose()
    } catch {
      setIsSubmitting(false)
    }
  }

  return (
    <NativeModal dividers={[]} open={open} onClose={onClose}>
      <NativeModal.Header title={t(`portal.servicepoints.${typeKey}.closerByTitle`)} onClose={onClose} />
      <NativeModal.Content noPaddingSides>
        <Paper hasNoOverflowHidden background="dark">
          <StackColumn gap={3}>
            <ServicepointCloserByContent isLoading={isLoading} servicepoint={servicepoint} typeKey={typeKey} />
          </StackColumn>
        </Paper>
      </NativeModal.Content>
      <NativeModal.Footer>
        <Button
          color="primary"
          fullWidth={isMobileVariant}
          sx={{ ml: 'auto' }}
          variant="contained"
          loading={isSubmitting}
          onClick={handleCtaClick}
        >
          {t('portal.servicepoints.ctaButton')}
        </Button>
      </NativeModal.Footer>
    </NativeModal>
  )
}
